import moment, { Moment } from 'moment-timezone';
import { useState } from 'react';
import {
  TAssignOrder, TCommissionPlan, TMember, TOrder, TOrderMapping,
} from '../../../../types';
import { calculateOrderCommissionByPlan } from '../../../../utils/helpers/orders.helper';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getMembers } from '../../../../store/team/selectors';
import { getCommissionPlans, getCommissionTeams } from '../../../../store/commissions/selectors';
import { getOrdersChosenDates } from '../../../../store/orders/selectors';
import { ordersActions } from '../../../../store/orders/slice';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

export interface IOrderMapping {
  orderId: string;
  commissionPlan?: any;
  team?: string;
}

export function useOrders() {
  const ordersChosenDates = useAppSelector(getOrdersChosenDates);
  const dispatch = useAppDispatch();
  const { shop } = useAppSelector(getAuthenticateUser);
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);

  const getOrderData = (orders: Array<TAssignOrder>) => orders.reduce((acc, order) => {
    if (order.member?.email) {
      const { email } = order.member;

      if (!acc[email]) {
        acc[email] = [];
      }

      acc[email].push({
        orderId: String(order.id),
        commissionPlan: order.commissionPlan,
        team: order.team,
      });
    }

    return acc;
  }, {} as { [email: string]: Array<IOrderMapping> });

  const getOrdersDiff = (orders: Array<TOrder>, attributedOrders: Array<TAssignOrder>) => {
    const ordersDiff = orders.filter((order) => !attributedOrders.some((attOrder) => String(order.id) === String(attOrder.orderId)));

    return ordersDiff.reduce((acc, order) => {
      const orderWithOrderId = attributedOrders.find((attOrder) => String(attOrder.userId) === String(order.userId));

      if (!orderWithOrderId) {
        acc.push(order);
      } else {
        acc.push({
          ...order,
          member: orderWithOrderId.member || {} as TMember,
        });
      }

      return acc;
    }, [] as Array<TOrder>);
  };

  const ordersSetDates = (dates: [Moment, Moment]) => {
    // Check if both dates are valid
    if (!dates[0]?.isValid() || !dates[1]?.isValid()) {
      console.error('Invalid date(s) provided');
      setIsBtnDisabled(true); // Disable button if dates are invalid
      return;
    }

    const prevEndDate = moment.tz(ordersChosenDates[1], shop.shopTimezone)?.format();
    const newEndDate = moment.tz(dates[1], shop.shopTimezone)?.endOf('day').format();
    const prevStartDate = moment.tz(ordersChosenDates[0], shop.shopTimezone)?.format();
    const newStartDate = moment.tz(dates[0], shop.shopTimezone)?.startOf('day').format();

    if (prevStartDate !== newStartDate || prevEndDate !== newEndDate) {
      setIsBtnDisabled(false);
      dispatch(ordersActions.setOrdersChosenDates(dates));
    } else {
      setIsBtnDisabled(true);
    }
  };

  return {
    getOrderData,
    ordersSetDates,
    isBtnDisabled,
    getOrdersDiff,
    setIsBtnDisabled,
  };
}

export function useMergeCommission() {
  const members = useAppSelector(getMembers);
  const commissionPlans = useAppSelector(getCommissionPlans);
  const commissionTeams = useAppSelector(getCommissionTeams);

  const mergeCommissionPlansIntoOrders = (
    orders: Array<TAssignOrder>,
    mappingOrders: Array<TOrderMapping>,
  ) => {
    const orderIdToMappingMap = new Map<string, typeof mappingOrders[number]>();

    // eslint-disable-next-line no-restricted-syntax
    for (const mapping of mappingOrders) {
      // eslint-disable-next-line no-restricted-syntax
      for (const order of mapping.orders) {
        orderIdToMappingMap.set(order.orderId.toString(), mapping);
      }
    }

    return orders.map((order) => {
      const clonedOrder = { ...order };

      const matchedMember = members.find((m) => (order?.userId != null && String(m.shopifyId) === String(order.userId))
      || (Array.isArray(m.commissionTags)
        && order.tags.some((tag) => m.commissionTags.includes(tag))));

      const matchedTeam = commissionTeams.find((team) => team.members.some((member) => member.email === matchedMember?.email));

      if (matchedTeam) {
        clonedOrder.commissionPlan = matchedTeam.commissionPlan;
        clonedOrder.isCommissionTeam = true;
        clonedOrder.member = matchedMember;
        clonedOrder.teamId = matchedTeam.id;
        clonedOrder.originalId = clonedOrder.id;

        const existsPlan = commissionPlans.find((plan) => plan.id === clonedOrder.commissionPlan?.id);
        if (existsPlan?.id) {
          const commissionResult = calculateOrderCommissionByPlan(clonedOrder.summary.netSales, matchedTeam.commissionPlan, clonedOrder.lineItems);
          clonedOrder.commission = commissionResult.totalCommission;
          clonedOrder.commissionData = commissionResult.commissionData;
        } else {
          clonedOrder.commission = 0;
        }
      } else if (matchedMember) {
      // Continue with original member-based logic
        clonedOrder.member = matchedMember;
        if (matchedMember.commissionPlan) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore

          clonedOrder.commissionPlan = matchedMember.commissionPlan;

          if (matchedMember.commissionPlan?.id) {
            const commissionResult = calculateOrderCommissionByPlan(clonedOrder.summary.netSales, matchedMember.commissionPlan, clonedOrder.lineItems);
            clonedOrder.commission = commissionResult.totalCommission;
            clonedOrder.commissionData = commissionResult.commissionData;
          } else {
            clonedOrder.commission = 0;
          }
        }
      }

      const matchedMapping = orderIdToMappingMap.get(clonedOrder.id.toString());

      if (matchedMapping) {
        const matchedMappingMember = members.find((member) => member.email === matchedMapping.email);

        clonedOrder.commissionPlan = matchedMappingMember?.commissionPlan as TCommissionPlan;

        if (matchedMappingMember?.commissionPlan?.id) {
          const commissionResult = calculateOrderCommissionByPlan(clonedOrder.summary.netSales, matchedMappingMember?.commissionPlan, clonedOrder.lineItems);

          clonedOrder.commission = commissionResult.totalCommission;
          clonedOrder.commissionData = commissionResult.commissionData;
        } else {
          clonedOrder.commission = 0;
        }

        clonedOrder.userId = matchedMappingMember?.shopifyId || 0;
        if (matchedMappingMember) {
          clonedOrder.member = matchedMappingMember;
        }
      }

      return clonedOrder;
    });
  };

  return {
    mergeCommissionPlansIntoOrders,
  };
}
